<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container pim-report-switcher">
      <div class="pim-report-switcher-header">
        <div>
          <h6 class="oxd-text oxd-text--h6 orangehrm-main-title">
            PIM Reports
          </h6>
          <div class="pim-report-tabs" aria-label="PIM reports">
            <button
              type="button"
              class="pim-report-tab"
              :class="{active: activeReport === 'employee'}"
              @click="activeReport = 'employee'"
            >
              Employee Reports
            </button>
            <button
              type="button"
              class="pim-report-tab"
              :class="{active: activeReport === 'birthday'}"
              @click="activeReport = 'birthday'"
            >
              Monthly Birthday Report
            </button>
            <button
              type="button"
              class="pim-report-tab"
              :class="{active: activeReport === 'documents'}"
              @click="activeReport = 'documents'"
            >
              Expiring Documents Report
            </button>
            <a
              class="pim-report-tab pim-report-link"
              :href="bulkImportExportUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              PIM Bulk Import / Export
            </a>
          </div>
        </div>
        <a
          v-if="activeReport !== 'employee'"
          class="oxd-button oxd-button--medium oxd-button--secondary"
          :href="activeReportUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Full Page
        </a>
      </div>
    </div>

    <template v-if="activeReport === 'employee'">
      <oxd-table-filter :filter-title="$t('general.employee_reports')">
        <oxd-form @submit-valid="filterItems">
          <oxd-form-row>
            <oxd-grid :cols="2" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <report-autocomplete
                  v-model="filters.report"
                  :rules="rules.report"
                ></report-autocomplete>
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <oxd-button
              display-type="ghost"
              :label="$t('general.reset')"
              @click="onClickReset"
            />
            <oxd-button
              class="orangehrm-left-space"
              display-type="secondary"
              :label="$t('general.search')"
              type="submit"
            />
          </oxd-form-actions>
        </oxd-form>
      </oxd-table-filter>

      <br />

      <div class="orangehrm-paper-container">
        <div class="orangehrm-header-container">
          <oxd-button
            :label="$t('general.add')"
            icon-name="plus"
            display-type="secondary"
            @click="onClickAdd"
          />
        </div>
        <table-header
          :selected="checkedItems.length"
          :total="total"
          :loading="isLoading"
          @delete="onClickDeleteSelected"
        ></table-header>
        <div class="orangehrm-container">
          <oxd-card-table
            v-model:selected="checkedItems"
            v-model:order="sortDefinition"
            :headers="headers"
            :items="items?.data"
            :selectable="true"
            :clickable="false"
            :loading="isLoading"
            row-decorator="oxd-table-decorator-card"
          />
        </div>
        <div class="orangehrm-bottom-container">
          <oxd-pagination
            v-if="showPaginator"
            v-model:current="currentPage"
            :length="pages"
          />
        </div>
      </div>
    </template>

    <div v-else class="orangehrm-paper-container pim-birthday-report-container">
      <iframe
        class="pim-birthday-report-frame"
        :title="activeReportTitle"
        :src="activeEmbeddedReportUrl"
      ></iframe>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {validSelection} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';
import {navigate} from '@ohrm/core/util/helper/navigation';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import ReportAutocomplete from '@/orangehrmPimPlugin/components/ReportAutocomplete';

const defaultFilters = {
  report: null,
};

const defaultSortOrder = {
  'report.name': 'ASC',
};

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'report-autocomplete': ReportAutocomplete,
  },

  setup() {
    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });
    const filters = ref({...defaultFilters});
    const serializedFilters = computed(() => {
      return {
        reportId: filters.value.report?.id,
        sortField: sortField.value,
        sortOrder: sortOrder.value,
      };
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/reports/defined',
    );
    const activeReport = ref('employee');
    const basePath = computed(() => {
      const path = window.location.pathname;
      const webIndex = path.indexOf('/web/index.php');
      if (webIndex !== -1) {
        return path.slice(0, webIndex + '/web'.length);
      }

      const indexPhp = path.indexOf('/index.php');
      return indexPhp === -1 ? '' : path.slice(0, indexPhp);
    });
    const birthdayReportUrl = computed(() => {
      return `${basePath.value}/monthly-birthday-report.php`;
    });
    const birthdayEmbeddedReportUrl = computed(() => {
      return `${birthdayReportUrl.value}?embedded=1`;
    });
    const documentReportUrl = computed(() => {
      return `${basePath.value}/expiring-documents-report.php`;
    });
    const documentEmbeddedReportUrl = computed(() => {
      return `${documentReportUrl.value}?embedded=1`;
    });
    const bulkImportExportUrl = computed(() => {
      return `${basePath.value}/group-hr-pim-bulk.php`;
    });
    const activeReportUrl = computed(() => {
      return activeReport.value === 'documents'
        ? documentReportUrl.value
        : birthdayReportUrl.value;
    });
    const activeEmbeddedReportUrl = computed(() => {
      return activeReport.value === 'documents'
        ? documentEmbeddedReportUrl.value
        : birthdayEmbeddedReportUrl.value;
    });
    const activeReportTitle = computed(() => {
      return activeReport.value === 'documents'
        ? 'Expiring Documents Report'
        : 'Monthly Birthday Report';
    });
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
    });

    onSort(execQuery);

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      sortDefinition,
      activeReport,
      birthdayReportUrl,
      birthdayEmbeddedReportUrl,
      documentReportUrl,
      documentEmbeddedReportUrl,
      bulkImportExportUrl,
      activeReportUrl,
      activeEmbeddedReportUrl,
      activeReportTitle,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'name',
          slot: 'title',
          title: this.$t('general.name'),
          style: {flex: '85%'},
          sortField: 'report.name',
        },
        {
          name: 'actions',
          title: this.$t('general.actions'),
          slot: 'action',
          style: {flex: '15%'},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            delete: {
              onClick: this.onClickDelete,
              component: 'oxd-icon-button',
              props: {
                name: 'trash',
              },
            },
            edit: {
              onClick: this.onClickEdit,
              props: {
                name: 'pencil-fill',
              },
            },
            view: {
              onClick: this.onClickView,
              props: {
                name: 'file-text-fill',
              },
            },
          },
        },
      ],
      checkedItems: [],
      rules: {
        report: [validSelection],
      },
    };
  },

  methods: {
    onClickAdd() {
      navigate('/pim/definePredefinedReport');
    },
    onClickEdit(item) {
      navigate('/pim/definePredefinedReport/{id}', {id: item.id});
    },
    onClickView(item) {
      navigate('/pim/displayPredefinedReport/{id}', {id: item.id});
    },
    onClickDeleteSelected() {
      const ids = [];
      this.checkedItems.forEach((index) => {
        ids.push(this.items?.data[index].id);
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
    onClickReset() {
      this.filters = {...defaultFilters};
      this.filterItems();
    },
  },
};
</script>

<style lang="scss" scoped>
.pim-report-switcher {
  margin-bottom: 1rem;
  padding: 0;
  overflow: hidden;
}

.pim-report-switcher-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem;
}

.pim-report-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.pim-report-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.1rem;
  border: 1px solid #dfe3e8;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #38455d;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0 0.8rem;
}

.pim-report-link {
  text-decoration: none;
}

.pim-report-tab.active {
  background: var(--oxd-primary-one-color);
  border-color: var(--oxd-primary-one-color);
  color: #ffffff;
}

.pim-birthday-report-container {
  padding: 0;
  overflow: hidden;
}

.pim-birthday-report-frame {
  display: block;
  width: 100%;
  height: calc(100vh - 250px);
  min-height: 560px;
  border: 0;
  background: #fff;
}

@media (max-width: 800px) {
  .pim-report-switcher-header {
    align-items: stretch;
    flex-direction: column;
  }

  .pim-birthday-report-frame {
    height: calc(100vh - 310px);
  }
}
</style>
